// ─── Workshop Truck Availability Tracker ───
// StatusHistory view – Status change audit trail with date range filtering
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useMemo, useEffect, useCallback } from 'react'
import { format, subDays, startOfMonth } from 'date-fns'
import {
  Activity,
  Loader2,
  ChevronLeft,
  ChevronRight,
  SearchX,
  Search,
  Download,
  Filter,
  TrendingUp,
  Clock,
  Users,
  RefreshCw,
} from 'lucide-react'

import type { StatusHistoryEntry, WorkshopStatus, ToastMessage } from '@/types'
import StatusBadge from '@/components/ui/StatusBadge'
import { formatDateTime, STATUS_COLORS } from '@/utils/helpers'
import { api } from '@/utils/api'

// ─── Props ──────────────────────────────────────────────────

interface StatusHistoryProps {
  addToast: (type: ToastMessage['type'], message: string) => void
}

// ─── Client colour badges ───────────────────────────────────

const CLIENT_COLORS: Record<string, string> = {
  LAPW: 'bg-blue-100 text-blue-800',
  TSL: 'bg-green-100 text-green-800',
  LAPE: 'bg-purple-100 text-purple-800',
  TNP: 'bg-orange-100 text-orange-800',
}

function clientBadgeClass(name: string): string {
  const key = Object.keys(CLIENT_COLORS).find((k) =>
    (name ?? '').toUpperCase().includes(k),
  )
  return key ? CLIENT_COLORS[key] : 'bg-gray-100 text-gray-700'
}

// ─── Date helpers ───────────────────────────────────────────

function toISO(d: Date): string { return format(d, 'yyyy-MM-dd') }
function todayISO(): string { return toISO(new Date()) }

type QuickRange = 'today' | '7d' | '30d' | 'month'

function getQuickRange(key: QuickRange): { from: string; to: string } {
  const today = new Date()
  switch (key) {
    case 'today': return { from: todayISO(), to: todayISO() }
    case '7d': return { from: toISO(subDays(today, 7)), to: todayISO() }
    case '30d': return { from: toISO(subDays(today, 30)), to: todayISO() }
    case 'month': return { from: toISO(startOfMonth(today)), to: todayISO() }
  }
}

interface ApiStatusHistoryResponse {
  success: boolean
  data: StatusHistoryEntry[]
  count: number
}

const PAGE_SIZE = 50
const ALL_STATUSES_FILTER = [
  'Active Fleet', 'In Assessment', 'Awaiting Parts',
  'Work In Progress', 'Ready for Release', 'Released',
] as const

// ── CSV export helper ─────────────────────────────────────────

function exportToCSV(data: StatusHistoryEntry[], from: string, to: string) {
  const header = 'Date/Time,Fleet #,Client,Status,Updated By'
  const rows = data.map((e) =>
    [
      `"${formatDateTime(e.recorded_at)}"`,
      `"${e.registration}"`,
      `"${e.client_name ?? ''}"`,
      `"${e.status}"`,
      `"${e.updated_by}"`,
    ].join(','),
  )
  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `status-history_${from}_to_${to}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── KPI Card ──────────────────────────────────────────────────

function KpiCard({
  icon: Icon,
  label,
  value,
  color,
  sub,
}: {
  icon: React.ElementType
  label: string
  value: string | number
  color: string
  sub?: string
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex items-start gap-3">
      <div className={`p-2.5 rounded-lg ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-gray-800 leading-tight">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════
// COMPONENT
// ═════════════════════════════════════════════════════════════

export default function StatusHistory({ addToast }: StatusHistoryProps) {
  // ── Date range state ──────────────────────────────────────
  const defaultRange = getQuickRange('30d')
  const [fromDate, setFromDate] = useState(defaultRange.from)
  const [toDate, setToDate] = useState(defaultRange.to)
  const [activeQuick, setActiveQuick] = useState<QuickRange | null>('30d')

  const applyQuick = (key: QuickRange) => {
    const range = getQuickRange(key)
    setFromDate(range.from)
    setToDate(range.to)
    setActiveQuick(key)
  }

  const handleFromChange = (v: string) => { setFromDate(v); setActiveQuick(null) }
  const handleToChange = (v: string) => { setToDate(v); setActiveQuick(null) }

  // ── Search / filter state ─────────────────────────────────
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterClient, setFilterClient] = useState('')

  // ── Status history fetch ──────────────────────────────────
  const [statusHistory, setStatusHistory] = useState<StatusHistoryEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)

  const fetchStatusHistory = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await api.get<ApiStatusHistoryResponse>(
        `/api/status-history?from=${fromDate}&to=${toDate}`,
      )
      setStatusHistory(res.data ?? [])
      setPage(1)
    } catch {
      setStatusHistory([])
      addToast('error', 'Failed to load status history')
    } finally {
      setIsLoading(false)
    }
  }, [fromDate, toDate, addToast])

  useEffect(() => { fetchStatusHistory() }, [fetchStatusHistory])

  // ── Filtered data ─────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return statusHistory.filter((e) => {
      if (filterStatus && e.status !== filterStatus) return false
      if (filterClient && !(e.client_name ?? '').toUpperCase().includes(filterClient)) return false
      if (q && !(e.registration ?? '').toLowerCase().includes(q) &&
        !(e.updated_by ?? '').toLowerCase().includes(q)) return false
      return true
    })
  }, [statusHistory, search, filterStatus, filterClient])

  // ── KPI derived values ─────────────────────────────────────
  const kpis = useMemo(() => {
    const total = filtered.length
    const uniqueVehicles = new Set(filtered.map((e) => e.registration)).size
    const uniqueUsers = new Set(filtered.map((e) => e.updated_by)).size
    const mostCommon = filtered.reduce<Record<string, number>>((acc, e) => {
      acc[e.status] = (acc[e.status] ?? 0) + 1
      return acc
    }, {})
    const topStatus = Object.entries(mostCommon).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—'
    return { total, uniqueVehicles, uniqueUsers, topStatus }
  }, [filtered])

  // ── Unique clients for filter dropdown ────────────────────
  const uniqueClients = useMemo(() => {
    const keys: string[] = []
    statusHistory.forEach((e) => {
      if (e.client_name) {
        const k = Object.keys(CLIENT_COLORS).find((c) =>
          (e.client_name ?? '').toUpperCase().includes(c),
        )
        if (k && !keys.includes(k)) keys.push(k)
      }
    })
    return keys.sort()
  }, [statusHistory])

  // ── Pagination ────────────────────────────────────────────
  const total = filtered.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const startIdx = (safePage - 1) * PAGE_SIZE
  const endIdx = Math.min(startIdx + PAGE_SIZE, total)
  const pageData = filtered.slice(startIdx, endIdx)

  const pageButtons = useMemo(() => {
    const pages: (number | '...')[] = []
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (safePage > 3) pages.push('...')
      const s = Math.max(2, safePage - 1)
      const e = Math.min(totalPages - 1, safePage + 1)
      for (let i = s; i <= e; i++) pages.push(i)
      if (safePage < totalPages - 2) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }, [safePage, totalPages])

  const quickButtons: { key: QuickRange; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: '7d', label: 'Last 7 Days' },
    { key: '30d', label: 'Last 30 Days' },
    { key: 'month', label: 'This Month' },
  ]

  // ═════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════

  return (
    <div className="space-y-6">
      {/* ═══ PAGE HEADER ═════════════════════════════════════ */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Status Change History</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Complete audit trail of all fleet status changes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportToCSV(filtered, fromDate, toDate)}
            disabled={filtered.length === 0}
            className="flex items-center gap-1.5 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm
                       hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={15} />
            Export CSV
          </button>
          <button
            onClick={fetchStatusHistory}
            disabled={isLoading}
            className="flex items-center gap-1.5 bg-[#1E2A3A] text-white px-3 py-2 rounded-lg text-sm
                       hover:bg-[#253347] transition-colors disabled:opacity-60"
          >
            <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ═══ DATE RANGE SELECTOR ════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <p className="text-sm font-semibold text-gray-700 mb-3">Select Date Range</p>
        <div className="flex gap-2 mb-4 flex-wrap">
          {quickButtons.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => applyQuick(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${activeQuick === key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-4 items-end flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={fromDate}
              onChange={(e) => handleFromChange(e.target.value)} max={toDate}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={toDate}
              onChange={(e) => handleToChange(e.target.value)} min={fromDate} max={todayISO()}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <button
            onClick={fetchStatusHistory}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Generate Report
          </button>
        </div>
      </div>

      {/* ═══ KPI SUMMARY CARDS ══════════════════════════════ */}
      {!isLoading && statusHistory.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KpiCard icon={Activity} label="Total Events" value={kpis.total} color="bg-purple-500" sub="in selected range" />
          <KpiCard icon={TrendingUp} label="Vehicles Affected" value={kpis.uniqueVehicles} color="bg-blue-500" sub="unique fleet #s" />
          <KpiCard icon={Users} label="Updated By" value={kpis.uniqueUsers} color="bg-green-500" sub="unique operators" />
          <KpiCard icon={Clock} label="Most Common" value={kpis.topStatus} color="bg-amber-500" sub="status change" />
        </div>
      )}

      {/* ═══ SEARCH & FILTER BAR ════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm px-5 py-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search fleet # or operator…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-400" />
            <select
              value={filterStatus}
              title="Filter by status"
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1) }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              {ALL_STATUSES_FILTER.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              value={filterClient}
              title="Filter by client"
              onChange={(e) => { setFilterClient(e.target.value); setPage(1) }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Clients</option>
              {uniqueClients.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {(search || filterStatus || filterClient) && (
            <button
              onClick={() => { setSearch(''); setFilterStatus(''); setFilterClient(''); setPage(1) }}
              className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ═══ STATUS HISTORY TABLE ══════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Status Changes</h2>
          <div className="flex items-center gap-3">
            {isLoading && <Loader2 size={16} className="animate-spin text-blue-500" />}
            <span className="bg-purple-50 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full">
              {total.toLocaleString()} event{total !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['#', 'Date / Time', 'Fleet #', 'Client', 'Status', 'Updated By'].map((h) => (
                  <th key={h}
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && Array.from({ length: 8 }, (_, i) => (
                <tr key={`skel-${i}`} className="border-b border-gray-100 animate-pulse">
                  <td className="px-5 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                  <td className="px-5 py-3"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                  <td className="px-5 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                  <td className="px-5 py-3"><div className="h-4 bg-gray-200 rounded w-14" /></td>
                  <td className="px-5 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                  <td className="px-5 py-3"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                </tr>
              ))}

              {!isLoading && pageData.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <SearchX size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 font-medium mb-1">No status change events found</p>
                    <p className="text-gray-400 text-sm">Try expanding the date range or clearing filters</p>
                  </td>
                </tr>
              )}

              {!isLoading && pageData.map((entry, idx) => {
                const dotColor = STATUS_COLORS[entry.status as WorkshopStatus] ?? '#6B7280'
                const rowNum = startIdx + idx + 1
                return (
                  <tr key={entry.id}
                    className={`border-b border-gray-100 transition-colors hover:bg-blue-50/40
                      ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-5 py-3 text-xs text-gray-400 font-mono">
                      {rowNum}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">
                      {formatDateTime(entry.recorded_at)}
                    </td>
                    <td className="px-5 py-3">
                      {entry.registration ? (
                        <span className="inline-flex items-center gap-1.5 bg-[#1E2A3A] text-white text-xs font-bold px-2.5 py-1 rounded-lg">
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: dotColor }}
                          />
                          {entry.registration}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {entry.client_name ? (
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${clientBadgeClass(entry.client_name)}`}>
                          {entry.client_name}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={entry.status as WorkshopStatus} size="sm" />
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">
                      <span className={`${entry.updated_by === 'n8n-auto'
                        ? 'text-xs text-gray-400 italic'
                        : 'font-medium text-gray-700'}`}>
                        {entry.updated_by === 'n8n-auto' ? '🤖 n8n-auto' : entry.updated_by}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ───────────────────────────────────── */}
        {total > PAGE_SIZE && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <span className="text-sm text-gray-600">
              Showing <strong>{startIdx + 1}</strong>–<strong>{endIdx}</strong> of{' '}
              <strong>{total.toLocaleString()}</strong> events
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex gap-1">
                {pageButtons.map((p, i) =>
                  p === '...' ? (
                    <span key={`ell-${i}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`min-w-[32px] h-8 rounded-md text-sm font-medium transition-colors
                        ${safePage === p
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
